import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { User, Mail, Calendar, Shield, Edit, Save, X, Camera, Trash2, Lock, Eye, EyeOff, Phone, MapPin, FileText } from 'lucide-react';
import api from '../services/api';

function Profile() {
  const { user, updateProfile, updateAvatar, removeAvatar, changePassword } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const fileInputRef = useRef(null);

  // Estado para campos editáveis do perfil
  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '',
    username: user?.username || '',
    email: user?.email || '',
    bio: user?.bio || '',
    phone: user?.phone || '',
    address: user?.address || '',
    timezone: user?.timezone || 'America/Sao_Paulo'
  });

  // Estado para controlar campos editáveis
  const [editableFields, setEditableFields] = useState({
    fullName: false,
    username: false,
    email: false,
    bio: false,
    phone: false,
    address: false,
    timezone: false
  });

  // Estado para salvar alterações
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [loadingTimezones, setLoadingTimezones] = useState(false);



  // Função para selecionar arquivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem.');
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 5MB.');
        return;
      }

      setSelectedFile(file);
      
      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para fazer upload do avatar
  const handleAvatarUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const result = await updateAvatar(previewUrl);
      if (result.success) {
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Função para remover avatar
  const handleRemoveAvatar = async () => {
    if (!user?.avatar_base64) return;

    if (window.confirm('Tem certeza que deseja remover seu avatar?')) {
      try {
        await removeAvatar();
      } catch (error) {
        console.error('Erro ao remover avatar:', error);
      }
    }
  };

  // Função para abrir seletor de arquivo
  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  // Função para tornar campo editável
  const makeFieldEditable = (fieldName) => {
    setEditableFields(prev => ({ ...prev, [fieldName]: true }));
  };

  // Função para cancelar edição de campo individual
  const cancelFieldEdit = (fieldName) => {
    setProfileData(prev => ({ ...prev, [fieldName]: user?.[fieldName] || '' }));
    setEditableFields(prev => ({ ...prev, [fieldName]: false }));
    // Não resetar hasChanges aqui, pois outros campos podem ter mudanças
  };

  // Função para salvar alterações de campo individual (opcional)
  const saveFieldEdit = async (fieldName) => {
    if (profileData[fieldName] === user?.[fieldName]) {
      setEditableFields(prev => ({ ...prev, [fieldName]: false }));
      return;
    }

    try {
      const updateData = { [fieldName]: profileData[fieldName] };
      const result = await updateProfile(updateData);
      
      if (result.success) {
        setEditableFields(prev => ({ ...prev, [fieldName]: false }));
        // Verificar se ainda há mudanças em outros campos
        const otherFieldsChanged = Object.keys(profileData).some(key => 
          key !== fieldName && profileData[key] !== user?.[key]
        );
        setHasChanges(otherFieldsChanged);
      }
    } catch (error) {
      console.error('Erro ao salvar campo:', error);
    }
  };

  // Função para salvar todas as alterações
  const saveAllChanges = async () => {
    setIsSaving(true);
    try {
      const result = await updateProfile(profileData);
      if (result.success) {
        setHasChanges(false);
        // Atualizar campos editáveis para false
        setEditableFields({
          fullName: false,
          username: false,
          email: false,
          bio: false,
          phone: false,
          address: false,
          timezone: false
        });
      }
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Função para cancelar todas as alterações
  const cancelAllChanges = () => {
    setProfileData({
      fullName: user?.fullName || '',
      username: user?.username || '',
      email: user?.email || '',
      bio: user?.bio || '',
      phone: user?.phone || '',
      address: user?.address || '',
      timezone: user?.timezone || 'America/Sao_Paulo'
    });
    setEditableFields({
      fullName: false,
      username: false,
      email: false,
      bio: false,
      phone: false,
      address: false,
      timezone: false
    });
    setHasChanges(false);
  };

  // Função para detectar mudanças
  const handleFieldChange = (fieldName, value) => {
    setProfileData(prev => ({ ...prev, [fieldName]: value }));
    
    // Verificar se há mudanças comparando com os dados originais do usuário
    const hasAnyChanges = Object.keys(profileData).some(key => {
      if (key === fieldName) {
        return value !== user?.[key];
      }
      return profileData[key] !== user?.[key];
    });
    
    setHasChanges(hasAnyChanges);
  };

  // Função para carregar timezones disponíveis
  const loadTimezones = async () => {
    try {
      setLoadingTimezones(true);
      const response = await api.get('/users/timezones');
      if (response.data.success) {
        setTimezones(response.data.timezones);
      }
    } catch (error) {
      console.error('Erro ao carregar timezones:', error);
    } finally {
      setLoadingTimezones(false);
    }
  };

  // useEffect para inicializar profileData quando user mudar
  React.useEffect(() => {
    if (user) {
      setProfileData({
        fullName: user.fullName || '',
        username: user.username || '',
        email: user.email || '',
        bio: user.bio || '',
        phone: user.phone || '',
        address: user.address || '',
        timezone: user.timezone || 'America/Sao_Paulo'
      });
    }
  }, [user]);

  // useEffect para carregar timezones
  useEffect(() => {
    loadTimezones();
  }, []);

  // Funções para alteração de senha
  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordErrors({});
    setShowPasswords({ current: false, new: false, confirm: false });
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordErrors({});
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (password) => {
    const errors = {};
    
    if (password.length < 8) {
      errors.length = 'Senha deve ter pelo menos 8 caracteres';
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      errors.lowercase = 'Senha deve conter pelo menos uma letra minúscula';
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.uppercase = 'Senha deve conter pelo menos uma letra maiúscula';
    }
    
    if (!/(?=.*\d)/.test(password)) {
      errors.number = 'Senha deve conter pelo menos um número';
    }
    
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.special = 'Senha deve conter pelo menos um caractere especial (@$!%*?&)';
    }
    
    return errors;
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    
    // Validar nova senha em tempo real
    if (field === 'newPassword') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
    
    // Limpar erro de confirmação se as senhas coincidirem
    if (field === 'confirmPassword' && value === passwordData.newPassword) {
      setPasswordErrors(prev => ({ ...prev, confirm: undefined }));
    }
  };

  const handlePasswordSubmit = async () => {
    // Validações
    if (!passwordData.currentPassword) {
      setPasswordErrors(prev => ({ ...prev, current: 'Senha atual é obrigatória' }));
      return;
    }
    
    if (!passwordData.newPassword) {
      setPasswordErrors(prev => ({ ...prev, new: 'Nova senha é obrigatória' }));
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordErrors(prev => ({ ...prev, confirm: 'As senhas não coincidem' }));
      return;
    }
    
    const newPasswordErrors = validatePassword(passwordData.newPassword);
    if (Object.keys(newPasswordErrors).length > 0) {
      setPasswordErrors(newPasswordErrors);
      return;
    }
    
    setIsChangingPassword(true);
    try {
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      if (result.success) {
        closePasswordModal();
        // O logout será feito automaticamente pelo AuthContext
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Meu Perfil</h1>
        <p className="text-gray-600">
          Gerencie suas informações pessoais e configurações da conta.
        </p>
        

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Info */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Informações Pessoais</h3>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo
                  </label>
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    {editableFields.fullName ? (
                      <>
                        <input
                          type="text"
                          value={profileData.fullName}
                          onChange={(e) => handleFieldChange('fullName', e.target.value)}
                          className="flex-1 px-3 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Seu nome completo"
                        />
                        <button 
                          onClick={() => saveFieldEdit('fullName')}
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
                          title="Salvar"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => cancelFieldEdit('fullName')}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={profileData.fullName || 'Nome não informado'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                          placeholder="Seu nome completo"
                          disabled
                        />
                        <button 
                          onClick={() => makeFieldEditable('fullName')}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    {editableFields.username ? (
                      <>
                        <input
                          type="text"
                          value={profileData.username}
                          onChange={(e) => handleFieldChange('username', e.target.value)}
                          className="flex-1 px-3 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Seu username"
                        />
                        <button 
                          onClick={() => saveFieldEdit('username')}
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
                          title="Salvar"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => cancelFieldEdit('username')}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={profileData.username || 'Username não informado'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                          placeholder="Seu username"
                          disabled
                        />
                        <button 
                          onClick={() => makeFieldEditable('username')}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={user?.email || profileData.email || ''}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                    disabled
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">O e-mail é o usado no login e não pode ser alterado.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    {editableFields.phone ? (
                      <>
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => handleFieldChange('phone', e.target.value)}
                          className="flex-1 px-3 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="(11) 99999-9999"
                        />
                        <button 
                          onClick={() => saveFieldEdit('phone')}
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
                          title="Salvar"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => cancelFieldEdit('phone')}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          type="tel"
                          value={profileData.phone || 'Telefone não informado'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                          placeholder="(11) 99999-9999"
                          disabled
                        />
                        <button 
                          onClick={() => makeFieldEditable('phone')}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio
                  </label>
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-gray-400" />
                    {editableFields.bio ? (
                      <>
                        <textarea
                          value={profileData.bio}
                          onChange={(e) => handleFieldChange('bio', e.target.value)}
                          className="flex-1 px-3 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Conte um pouco sobre você..."
                          rows="3"
                        />
                        <button 
                          onClick={() => saveFieldEdit('bio')}
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
                          title="Salvar"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => cancelFieldEdit('bio')}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <textarea
                          value={profileData.bio || 'Bio não informada'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                          placeholder="Conte um pouco sobre você..."
                          rows="3"
                          disabled
                        />
                        <button 
                          onClick={() => makeFieldEditable('bio')}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endereço
                  </label>
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    {editableFields.address ? (
                      <>
                        <input
                          type="text"
                          value={profileData.address}
                          onChange={(e) => handleFieldChange('address', e.target.value)}
                          className="flex-1 px-3 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Seu endereço completo"
                        />
                        <button 
                          onClick={() => saveFieldEdit('address')}
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
                          title="Salvar"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => cancelFieldEdit('address')}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={profileData.address || 'Endereço não informado'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                          placeholder="Seu endereço completo"
                          disabled
                        />
                        <button 
                          onClick={() => makeFieldEditable('address')}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    {editableFields.timezone ? (
                      <>
                        <select
                          value={profileData.timezone}
                          onChange={(e) => handleFieldChange('timezone', e.target.value)}
                          className="flex-1 px-3 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={loadingTimezones}
                        >
                          {loadingTimezones ? (
                            <option>Carregando timezones...</option>
                          ) : (
                            timezones.map(timezone => (
                              <option key={timezone.value} value={timezone.value}>
                                {timezone.label}
                              </option>
                            ))
                          )}
                        </select>
                        <button 
                          onClick={() => saveFieldEdit('timezone')}
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
                          title="Salvar"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => cancelFieldEdit('timezone')}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={profileData.timezone || 'Timezone não configurado'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                          disabled
                        />
                        <button 
                          onClick={() => makeFieldEditable('timezone')}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Função
                  </label>
                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      defaultValue={user?.role || 'Usuário'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Sua função"
                      disabled
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Membro desde
                  </label>
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      defaultValue={user?.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-BR') : 'N/A'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={cancelAllChanges}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <X className="h-4 w-4 inline mr-2" />
                  Cancelar
                </button>
                <button 
                  onClick={saveAllChanges}
                  disabled={!hasChanges || isSaving}
                  className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 ${
                    hasChanges && !isSaving
                      ? 'bg-primary-600 hover:bg-primary-700 cursor-pointer'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Save className="h-4 w-4 inline mr-2" />
                  {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo do Perfil</h3>
            
            <div className="space-y-4">
              <div className="text-center">
                {/* Avatar Section */}
                <div className="relative w-24 h-24 mx-auto mb-4">
                  {user?.avatar_base64 ? (
                    <img 
                      src={user.avatar_base64} 
                      alt="Avatar do usuário"
                      className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-gray-300 rounded-full flex items-center justify-center border-4 border-gray-200">
                      <User className="h-12 w-12 text-gray-600" />
                    </div>
                  )}
                  
                  {/* Botão de upload */}
                  <button
                    onClick={openFileSelector}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white hover:bg-primary-700 transition-colors duration-200 shadow-lg"
                    title="Alterar avatar"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                </div>

                <h4 className="text-lg font-medium text-gray-900">
                  {user?.fullName || user?.username}
                </h4>
                <p className="text-sm text-gray-500 capitalize">
                  {user?.role || 'Usuário'}
                </p>

                {/* Avatar Upload Section */}
                <div className="mt-4 space-y-3">
                  {/* Input de arquivo oculto */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {/* Preview da imagem selecionada */}
                  {previewUrl && (
                    <div className="space-y-2">
                      <div className="w-16 h-16 mx-auto">
                        <img 
                          src={previewUrl} 
                          alt="Preview"
                          className="w-16 h-16 rounded-full object-cover border-2 border-primary-300"
                        />
                      </div>
                      <div className="flex space-x-2 justify-center">
                        <button
                          onClick={handleAvatarUpload}
                          disabled={isUploading}
                          className="px-3 py-1 text-xs bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 transition-colors duration-200"
                        >
                          {isUploading ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedFile(null);
                            setPreviewUrl(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          className="px-3 py-1 text-xs bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors duration-200"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Botão para remover avatar existente */}
                  {user?.avatar_base64 && !previewUrl && (
                    <button
                      onClick={handleRemoveAvatar}
                      className="flex items-center justify-center space-x-2 px-3 py-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                    >
                      <Trash2 className="h-3 w-3" />
                      <span>Remover Avatar</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Status da conta:</span>
                    <span className={`font-medium ${user?.isActive ? 'text-green-600' : 'text-red-600'}`}>
                      {user?.isActive ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Último login:</span>
                    <span className="text-gray-900">
                      {user?.lastLogin 
                        ? new Date(user.lastLogin).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Nunca'
                      }
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-900 mb-3">Ações Rápidas</h5>
                <div className="space-y-2">
                  <button 
                    onClick={openPasswordModal}
                    className="w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-md transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Lock className="h-4 w-4" />
                    <span>Alterar senha</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Alteração de Senha */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Alterar Senha</h3>
              <button
                onClick={closePasswordModal}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Senha Atual */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha Atual
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      passwordErrors.current ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Digite sua senha atual"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordErrors.current && (
                  <p className="text-sm text-red-600 mt-1">{passwordErrors.current}</p>
                )}
              </div>

              {/* Nova Senha */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      Object.keys(passwordErrors).some(key => key !== 'current' && key !== 'confirm') ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Digite a nova senha"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                
                {/* Validações da nova senha */}
                {Object.keys(passwordErrors).some(key => key !== 'current' && key !== 'confirm') && (
                  <div className="mt-2 space-y-1">
                    {passwordErrors.length && (
                      <p className="text-sm text-red-600">{passwordErrors.length}</p>
                    )}
                    {passwordErrors.lowercase && (
                      <p className="text-sm text-red-600">{passwordErrors.lowercase}</p>
                    )}
                    {passwordErrors.uppercase && (
                      <p className="text-sm text-red-600">{passwordErrors.uppercase}</p>
                    )}
                    {passwordErrors.number && (
                      <p className="text-sm text-red-600">{passwordErrors.number}</p>
                    )}
                    {passwordErrors.special && (
                      <p className="text-sm text-red-600">{passwordErrors.special}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Confirmar Nova Senha */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      passwordErrors.confirm ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirme a nova senha"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordErrors.confirm && (
                  <p className="text-sm text-red-600 mt-1">{passwordErrors.confirm}</p>
                )}
              </div>
            </div>

            {/* Botões */}
            <div className="flex space-x-3 mt-6">
              <button
                onClick={closePasswordModal}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancelar
              </button>
              <button
                onClick={handlePasswordSubmit}
                disabled={isChangingPassword}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isChangingPassword ? 'Alterando...' : 'Alterar Senha'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Profile;
